// @ts-nocheck
import Editor from './../../../../../components/Editor/Editor';
import {
  CalendarBlank,
  Clock,
  FileText,
  MapPin,
  Hash
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { formatTimeLocale } from '../../../../../utils/dateUtility';
import { parseISO, format } from 'date-fns';
import { openMaps } from '../../../helpers/contact';
import { useTimeFormat } from '../../../../../hooks/useTimeFormat';
import * as PropTypes from 'prop-types';

/**
 * A service card component for train services.
 *
 * @param {Object} servicefields - The fields of the service, including train details.
 * @param {string} tripdetaileddayserviceid - The ID of the trip detailed day service.
 * @param {boolean} isPreview - Whether the service card is in library preview mode.
 * @param {boolean} isDesktop - Whether the view is on a desktop device.
 * @returns {JSX.Element} - The JSX element representing a train service card.
 */
const TrainCard = ({
  servicefields,
  tripdetaileddayserviceid,
  isPreview,
  isDesktop
}) => {
  const { t } = useTranslation(['services']);
  const { timeFormat } = useTimeFormat();

  //Preview Content
  if (isPreview) {
    return (
      <div>
        <div className='activity-card__header'>
          <h3 className='activity-card__title'>{servicefields?.TITLE || ''}</h3>
        </div>
        {servicefields?.DEPARTURETIME && (
          <div className='activity-card__time'>
            <Clock size={14} weight='regular' />
            <span>
              {formatTimeLocale(servicefields?.DEPARTURETIME, timeFormat)}
            </span>
          </div>
        )}

        {servicefields?.TRAINCOMPANY ||
        servicefields?.CONFIRMATIONINFORMATION ? (
          <div className='activity-card__description-container'>
            {servicefields?.TRAINCOMPANY && (
              <div>
                <p className='activity-card__description'>
                  {servicefields.TRAINCOMPANY || ''}
                </p>
              </div>
            )}

            {servicefields?.CONFIRMATIONINFORMATION && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {servicefields.CONFIRMATIONINFORMATION || ''}
              </div>
            )}
          </div>
        ) : null}

        {isDesktop && (
          <>
            <div className='activity-card__desktop-preview'>
              {servicefields?.DEPARTUREDATE ||
              servicefields?.DEPARTURETIME ||
              servicefields?.DEPARTUREADDRESS ? (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:departure', 'Departure')}
                  </p>

                  {servicefields?.DEPARTUREDATE && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:date', 'Date')}:
                        </span>{' '}
                        {format(
                          parseISO(
                            servicefields.DEPARTUREDATE?.split('T')?.[0]
                          ),
                          'MMM d, yyyy'
                        )}
                      </p>
                    </div>
                  )}

                  {servicefields?.DEPARTURETIME && (
                    <div className='activity-card__modal-field__item'>
                      <Clock size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:time', 'Time')}:
                        </span>{' '}
                        {formatTimeLocale(
                          servicefields.DEPARTURETIME,
                          timeFormat
                        )}
                      </p>
                    </div>
                  )}

                  {servicefields?.DEPARTUREADDRESS && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(servicefields.DEPARTUREADDRESS);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>
                          {servicefields?.DEPARTUREADDRESS || ''}
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              ) : null}

              {servicefields?.ARRIVALDATE ||
              servicefields?.ARRIVALTIME ||
              servicefields?.ARRIVALADDRESS ? (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:arrival', 'Arrival')}
                  </p>

                  {servicefields?.ARRIVALDATE && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:date', 'Date')}:
                        </span>{' '}
                        {format(
                          parseISO(servicefields.ARRIVALDATE?.split('T')[0]),
                          'MMM d, yyyy'
                        )}
                      </p>
                    </div>
                  )}

                  {servicefields?.ARRIVALTIME && (
                    <div className='activity-card__modal-field__item'>
                      <Clock size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:time', 'Time')}:
                        </span>{' '}
                        {formatTimeLocale(
                          servicefields.ARRIVALTIME,
                          timeFormat
                        )}
                      </p>
                    </div>
                  )}

                  {servicefields?.ARRIVALADDRESS && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(servicefields.ARRIVALADDRESS);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>
                          {servicefields?.ARRIVALADDRESS || ''}
                        </span>
                      </a>
                    </div>
                  )}
                </div>
              ) : null}

              {servicefields?.TRAINNUMBER && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:trainNumber', 'Train Number')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <Hash size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {servicefields.TRAINNUMBER || ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {servicefields?.DESCRIPTION && (
              <div className='activity-card__modal-rich-text'>
                <Editor
                  id={`service-editor-${tripdetaileddayserviceid}-description`}
                  content={servicefields?.DESCRIPTION}
                  editable={false}
                  isPreviewing={true}
                />
              </div>
            )}
            {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
              <div className='activity-card__modal-field'>
                <p className='activity-card__modal-field__title'>
                  {t('services:attachedDocument', 'Attached Document')}
                </p>
                <div>
                  {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                    <a
                      key={idx}
                      href={attachment?.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='activity-card__attachment-link'
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <FileText size={20} />
                      <span>{attachment?.originalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  //Modal Content
  return (
    <div id={`service-card-train-${tripdetaileddayserviceid}`}>
      <div>
        {servicefields?.CONFIRMATIONINFORMATION && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:confirmationLabel', 'Confirmation')}
            </p>
            <div className='activity-card__modal__confirmation'>
              {servicefields.CONFIRMATIONINFORMATION || ''}
            </div>
          </div>
        )}

        {servicefields?.TRAINCOMPANY && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:provider', 'Provider')}
            </p>
            <p className='activity-card__modal-field__value'>
              {servicefields.TRAINCOMPANY || ''}
            </p>
          </div>
        )}

        {servicefields?.DEPARTUREDATE ||
        servicefields?.DEPARTURETIME ||
        servicefields?.DEPARTUREADDRESS ? (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:departure', 'Departure')}
            </p>

            {servicefields?.DEPARTUREDATE && (
              <div className='activity-card__modal-field__item'>
                <CalendarBlank size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:date', 'Date')}:
                  </span>{' '}
                  {format(
                    parseISO(servicefields.DEPARTUREDATE?.split('T')?.[0]),
                    'MMM d, yyyy'
                  )}
                </p>
              </div>
            )}

            {servicefields?.DEPARTURETIME && (
              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:time', 'Time')}:
                  </span>{' '}
                  {formatTimeLocale(servicefields.DEPARTURETIME, timeFormat)}
                </p>
              </div>
            )}

            {servicefields?.DEPARTUREADDRESS && (
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(servicefields.DEPARTUREADDRESS);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>
                    {servicefields?.DEPARTUREADDRESS || ''}
                  </span>
                </a>
              </div>
            )}
          </div>
        ) : null}

        {servicefields?.ARRIVALDATE ||
        servicefields?.ARRIVALTIME ||
        servicefields?.ARRIVALADDRESS ? (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:arrival', 'Arrival')}
            </p>

            {servicefields?.ARRIVALDATE && (
              <div className='activity-card__modal-field__item'>
                <CalendarBlank size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:date', 'Date')}:
                  </span>{' '}
                  {format(
                    parseISO(servicefields.ARRIVALDATE?.split('T')[0]),
                    'MMM d, yyyy'
                  )}
                </p>
              </div>
            )}

            {servicefields?.ARRIVALTIME && (
              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:time', 'Time')}:
                  </span>{' '}
                  {formatTimeLocale(servicefields.ARRIVALTIME, timeFormat)}
                </p>
              </div>
            )}

            {servicefields?.ARRIVALADDRESS && (
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(servicefields.ARRIVALADDRESS);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>
                    {servicefields?.ARRIVALADDRESS || ''}
                  </span>
                </a>
              </div>
            )}
          </div>
        ) : null}

        {servicefields?.TRAINNUMBER && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:trainNumber', 'Train Number')}
            </p>
            <div className='activity-card__modal-field__item'>
              <Hash size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {servicefields.TRAINNUMBER || ''}
              </p>
            </div>
          </div>
        )}

        {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:attachedDocument', 'Attached Document')}
            </p>
            <div>
              {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                <a
                  key={idx}
                  href={attachment?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='activity-card__attachment-link'
                >
                  <FileText size={20} />
                  <span>{attachment?.originalName}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {servicefields?.DESCRIPTION && (
          <div className='activity-card__modal-rich-text'>
            <Editor
              id={`service-editor-${tripdetaileddayserviceid}-description`}
              content={servicefields?.DESCRIPTION}
              editable={false}
              isPreviewing={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

TrainCard.propTypes = {
  servicefields: PropTypes.object,
  servicetype: PropTypes.object,
  tripdetaileddayserviceid: PropTypes.number,
  isPreview: PropTypes.bool,
  isDesktop: PropTypes.bool
};

export default TrainCard;
